import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuoteLeadOriginEnum, QuoteLeadOutput, QuoteLeadOutputV5 } from '@lx/quote-tool/shared/types';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { EMPTY, exhaustMap, Observable, switchMap, take, tap } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { GoogleAnalyticsErrorService } from '../../../google-analytics';
import { withErrorType } from '../../../mixins';
import { AyaLeadService, LeadService, NavigationService } from '../../../services';
import { QuoteToolLiteStore } from '../../../store';
import { Step } from '../../../types';
import { ApplicantDetailsComponent } from '../../applicant-details/applicant-details.component';
import { Flows } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  imports: [ApplicantDetailsComponent],
  selector: 'lx-applicant-step',
  templateUrl: './applicant-step.component.html',
  styleUrls: ['./applicant-step.component.scss'],
})
export class ApplicantStepComponent implements OnInit {
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly navigationService = inject(NavigationService);
  private readonly leadService = inject(LeadService);
  private readonly ayaLeadService = inject(AyaLeadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingService = inject(LoadingService);
  private readonly googleAnalyticsErrorService = inject(GoogleAnalyticsErrorService);
  private currentStep$: Observable<Step> | undefined;

  ngOnInit(): void {
    this.quoteToolLiteStore.updateValidityOfCurrentStep(true);
    this.currentStep$ = this.quoteToolLiteStore.currentStep$;
  }

  submitLead(): void {
    this.loadingService.startLoading($localize`Sending your quote...`);

    this.quoteToolLiteStore.activeFlow$
      .pipe(
        take(1),
        switchMap((flow) => this.requestLead(flow)),
        switchMap(() => this.currentStep$),
        catchError((error: HttpErrorResponse | unknown) => this.onLeadError(error)),
        finalize(() => this.loadingService.stopLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((step: Step) => {
        this.navigationService.navigateToNextStep(step);
      });
  }

  private requestLead(flow: Flows): Observable<QuoteLeadOutput | QuoteLeadOutputV5> {
    return flow === Flows.Aya ? this.createAyaLead() : this.createStandardLead();
  }

  private createAyaLead(): Observable<QuoteLeadOutput> {
    return this.quoteToolLiteStore.selectionForAyaLeads$.pipe(
      exhaustMap((selection) => this.ayaLeadService.createLead(selection, QuoteLeadOriginEnum.AYA_QUOTE)),
    );
  }

  private createStandardLead(): Observable<QuoteLeadOutputV5> {
    return this.quoteToolLiteStore.selectionForLeads$.pipe(
      take(1),
      exhaustMap((selection) => this.leadService.createLead(selection).pipe(tap((out: QuoteLeadOutputV5) => this.onLeadCreated(out)))),
    );
  }

  private onLeadCreated(out: QuoteLeadOutputV5): void {
    this.quoteToolLiteStore.updateState({ quoteId: out?.quoteId });
  }

  private onLeadError(error: HttpErrorResponse | unknown): Observable<never> {
    this.loadingService.stopLoading();
    const { getErrorType, getErrorMessage } = withErrorType();
    const type = getErrorType(error);
    const message = getErrorMessage(error);

    return this.currentStep$.pipe(
      take(1),
      tap((step: Step) => {
        this.googleAnalyticsErrorService.trackFormSubmissionFailure(step?.googleAnalyticsEvent, message, type);
      }),
      switchMap(() => EMPTY), // swallow error and complete
    );
  }
}
